import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as tf from '@tensorflow/tfjs';

// Arabic letter mapping like in the Python sample
const ARABIC_LETTERS = {
  Ain: 'ع',
  Al: 'ال',
  Alef: 'ا',
  Beh: 'ب',
  Dad: 'ض',
  Dal: 'د',
  Feh: 'ف',
  Ghain: 'غ',
  Hah: 'ح',
  Heh: 'ه',
  Jeem: 'ج',
  Kaf: 'ك',
  Khah: 'خ',
  Laa: 'لا',
  Lam: 'ل',
  Meem: 'م',
  Noon: 'ن',
  Qaf: 'ق',
  Reh: 'ر',
  Sad: 'ص',
  Seen: 'س',
  Sheen: 'ش',
  Tah: 'ط',
  Teh: 'ت',
  Teh_Marbuta: 'ة',
  Thal: 'ذ',
  Theh: 'ث',
  Waw: 'و',
  Yeh: 'ي',
  Zah: 'ظ',
  Zain: 'ز',
};

const CONFIDENCE_THRESHOLD = 0.6;
const STABILITY_FRAMES = 10;
const PAUSE_FRAMES = 15;

export default function SignDemo() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Model holder: we support either a TFJS LayersModel or a GraphModel
  const [model, setModel] = useState(null);
  const modelTypeRef = useRef('layers'); // 'layers' | 'graph'
  const [labels, setLabels] = useState([]);
  const [running, setRunning] = useState(false);

  const [currentLetterEn, setCurrentLetterEn] = useState('---');
  const [currentLetterAr, setCurrentLetterAr] = useState('---');
  const [confidence, setConfidence] = useState(0);

  const [detectedWordAr, setDetectedWordAr] = useState('');
  const [detectedWordEn, setDetectedWordEn] = useState([]);

  const stabilityCounterRef = useRef(0);
  const pauseCounterRef = useRef(0);
  const lastPredictionRef = useRef(null);
  const cameraRef = useRef(null);
  const handsRef = useRef(null);

  // Load model and labels on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Prefer WebGL if available
        if (tf.engine().backendName !== 'webgl' && tf.backend().name !== 'webgl') {
          try {
            await tf.setBackend('webgl');
            await tf.ready();
          } catch (_) {
            // Fallback to default backend
          }
        }

        // Build URLs relative to the public base (works in dev and in build with a non-root base)
        const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
        const modelUrl = `${base}/tfjs_model/model.json`;
        const labelsUrl = `${base}/tfjs_model/labels.json`;

        // Try to load as a Layers model first. If it fails with a Keras InputLayer error,
        // attempt to load as a Graph model (in case the export is SavedModel/Graph format).
        let loadedModel = null;
        try {
          loadedModel = await tf.loadLayersModel(modelUrl);
          modelTypeRef.current = 'layers';
          console.info('[ASL Demo] Loaded TFJS layers model:', modelUrl);
        } catch (layersErr) {
          console.warn('[ASL Demo] loadLayersModel failed, trying loadGraphModel...', layersErr);
          try {
            loadedModel = await tf.loadGraphModel(modelUrl);
            modelTypeRef.current = 'graph';
            console.info('[ASL Demo] Loaded TFJS graph model:', modelUrl, loadedModel?.inputs, loadedModel?.outputs);
          } catch (graphErr) {
            // Re-throw the original layers error if present, but include graph error details
            const combined = new Error(
              `Could not load model as layers or graph. Layers error: ${layersErr?.message}. Graph error: ${graphErr?.message}`
            );
            combined.layersErr = layersErr;
            combined.graphErr = graphErr;
            throw combined;
          }
        }

        // Load labels with explicit status checks and flexible parsing
        const labelsResp = await fetch(labelsUrl, { cache: 'no-store' });
        if (!labelsResp.ok) {
          throw new Error(`Labels fetch failed: ${labelsResp.status} ${labelsResp.statusText} @ ${labelsUrl}`);
        }
        let labelsJson;
        try {
          labelsJson = await labelsResp.json();
        } catch (e) {
          throw new Error(`Labels JSON parse error @ ${labelsUrl}: ${e?.message || e}`);
        }

        // Normalize labels format: accept ["A", ...] or {classes:[...]} or {index:name, ...}
        let normalizedLabels = [];
        if (Array.isArray(labelsJson)) {
          normalizedLabels = labelsJson;
        } else if (labelsJson && Array.isArray(labelsJson.classes)) {
          normalizedLabels = labelsJson.classes;
        } else if (labelsJson && typeof labelsJson === 'object') {
          // If it's an index->name map, sort by numeric keys
          const entries = Object.entries(labelsJson);
          const numeric = entries.every(([k]) => /^\d+$/.test(k));
          if (numeric) {
            normalizedLabels = entries
              .sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
              .map(([, v]) => v);
          }
        }

        if (!Array.isArray(normalizedLabels) || normalizedLabels.length === 0) {
          console.warn('Labels file format not recognized or empty. Raw value:', labelsJson);
          throw new Error('Labels file is empty or has an unsupported format.');
        }
        if (!mounted) return;
        setModel(loadedModel);
        setLabels(normalizedLabels);
      } catch (err) {
        console.error('[ASL Demo] Failed to load model or labels:', err);
        const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
        alert(
          `Failed to load model/labels.\n\nDetails in console.\nExpected files at:\n- ${base}/tfjs_model/model.json\n- ${base}/tfjs_model/labels.json\n\nIf you see an InputLayer error, your TFJS export may be missing the input shape. Re-export with an explicit Input(shape=(63,)).`
        );
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const clearAll = useCallback(() => {
    setDetectedWordAr('');
    setDetectedWordEn([]);
    stabilityCounterRef.current = 0;
    pauseCounterRef.current = 0;
    lastPredictionRef.current = null;
  }, []);

  const getArabic = useCallback((en) => ARABIC_LETTERS[en] || en, []);

  const addSpace = useCallback(() => {
    setDetectedWordAr((prev) => (prev.endsWith(' ') ? prev : prev + ' '));
    setDetectedWordEn((prev) => (prev[prev.length - 1] === 'SPACE' ? prev : [...prev, 'SPACE']));
  }, []);

  const deleteLastChar = useCallback(() => {
    setDetectedWordAr((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
    setDetectedWordEn((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
  }, []);

  // Prediction + stabilization logic
  const handlePrediction = useCallback(
    (enClass, conf) => {
      // pause window handling
      if (pauseCounterRef.current > 0) {
        pauseCounterRef.current -= 1;
        return;
      }

      // Special threshold for SPACE and DELETE
      const isSpecial = enClass === 'SPACE' || enClass === 'DELETE';
      const threshold = isSpecial ? 0.8 : CONFIDENCE_THRESHOLD;

      if (conf < threshold) {
        stabilityCounterRef.current = 0;
        lastPredictionRef.current = null;
        return;
      }

      if (lastPredictionRef.current === enClass) {
        stabilityCounterRef.current += 1;
      } else {
        lastPredictionRef.current = enClass;
        stabilityCounterRef.current = 1;
      }

      if (stabilityCounterRef.current >= STABILITY_FRAMES) {
        if (enClass === 'SPACE') {
          addSpace();
        } else if (enClass === 'DELETE') {
          deleteLastChar();
        } else {
          const ar = getArabic(enClass);
          setDetectedWordAr((prev) => (prev.endsWith(ar) ? prev : prev + ar));
          setDetectedWordEn((prev) => (prev[prev.length - 1] === enClass ? prev : [...prev, enClass]));
        }
        pauseCounterRef.current = PAUSE_FRAMES;
        stabilityCounterRef.current = 0;
      }
    },
    [getArabic, addSpace, deleteLastChar]
  );

  // Set up MediaPipe Hands and Camera when starting
  const start = useCallback(async () => {
    if (!model) {
      alert('Model not loaded yet. Please wait a moment…');
      return;
    }
    if (!videoRef.current) return;
    if (!window.Hands || !window.Camera || !window.drawConnectors || !window.drawLandmarks || !window.HAND_CONNECTIONS) {
      alert('MediaPipe scripts are still loading. Please wait a moment and try again.');
      return;
    }

    // Hands config
    const hands = new window.Hands({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`,
    });
    hands.setOptions({
      maxNumHands: 1,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      modelComplexity: 1,
    });

    hands.onResults(async (results) => {
      const video = videoRef.current;
      const canvasElement = canvasRef.current;
      if (!video || !canvasElement) return;
      const ctx = canvasElement.getContext('2d');

      // Set canvas size to video
      canvasElement.width = video.videoWidth;
      canvasElement.height = video.videoHeight;

      // Draw the image to canvas
      ctx.save();
      ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
      ctx.drawImage(results.image, 0, 0, canvasElement.width, canvasElement.height);

      let en = '---';
      let ar = '---';
      let conf = 0;

      if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
        const landmarks = results.multiHandLandmarks[0];
        // draw landmarks
        window.drawConnectors(ctx, landmarks, window.HAND_CONNECTIONS, { color: '#00FF00', lineWidth: 3 });
        window.drawLandmarks(ctx, landmarks, { color: '#FFFF00', lineWidth: 1, radius: 2 });

        // Flatten to [x,y,z]*21 => 63
        const flat = [];
        for (const lm of landmarks) {
          flat.push(lm.x, lm.y, lm.z ?? 0);
        }

        // Predict
        const logits = tf.tidy(() => {
          const input = tf.tensor(flat, [1, flat.length]);
          // Both LayersModel and GraphModel expose predict in TFJS 4.x. If not available, fallback to execute.
          if (typeof model?.predict === 'function') {
            return model.predict(input);
          }
          if (modelTypeRef.current === 'graph' && typeof model?.execute === 'function') {
            // Try default execution without specifying outputs (assumes single output)
            return model.execute(input);
          }
          throw new Error('Model does not support predict/execute.');
        });
        const data = await logits.data();
        logits.dispose();

        let maxIdx = 0;
        let maxVal = -Infinity;
        for (let i = 0; i < data.length; i++) {
          if (data[i] > maxVal) {
            maxVal = data[i];
            maxIdx = i;
          }
        }

        en = labels[maxIdx] || `Class ${maxIdx}`;
        ar = getArabic(en);
        conf = typeof maxVal === 'number' ? maxVal : 0;
        handlePrediction(en, conf);
      }

      // Update UI state
      setCurrentLetterEn(en);
      setCurrentLetterAr(ar);
      setConfidence(conf);

      ctx.restore();
    });

    // Camera setup
    const cam = new window.Camera(videoRef.current, {
      onFrame: async () => {
        await hands.send({ image: videoRef.current });
      },
      width: 640,
      height: 480,
    });

    handsRef.current = hands;
    cameraRef.current = cam;

    await cam.start();
    setRunning(true);
  }, [model, labels, handlePrediction, getArabic]);

  const stop = useCallback(() => {
    try {
      cameraRef.current?.stop();
    } catch (_) {}
    cameraRef.current = null;
    handsRef.current = null;
    setRunning(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try { cameraRef.current?.stop(); } catch (_) {}
    };
  }, []);

  return (
    <div style={{ fontFamily: 'system-ui, Arial, sans-serif', padding: 16 }}>
      <h2 style={{ marginTop: 0 }}>Arabic Sign Language — RTA Demo</h2>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <video ref={videoRef} style={{ display: 'none' }} autoPlay playsInline muted />
          <canvas ref={canvasRef} style={{ border: '1px solid #ddd', borderRadius: 8 }} />
        </div>

        <div style={{ minWidth: 320 }}>
          <div style={{ padding: 12, border: '1px solid #eee', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 14, color: '#666' }}>Current letter</div>
            <div style={{ fontSize: 36, fontWeight: 700 }}>
              {currentLetterAr}
              <span style={{ fontSize: 16, color: '#888', marginLeft: 8 }}>({currentLetterEn})</span>
            </div>
            <div style={{ marginTop: 8 }}>Confidence: {(confidence * 100).toFixed(1)}%</div>
            <div style={{ marginTop: 8 }}>
              Stability: {stabilityCounterRef.current}/{STABILITY_FRAMES}
              {pauseCounterRef.current > 0 && (
                <span style={{ marginLeft: 8 }}>
                  • Pause {pauseCounterRef.current}/{PAUSE_FRAMES}
                </span>
              )}
            </div>
          </div>

          <div style={{ padding: 12, border: '1px solid #eee', borderRadius: 8 }}>
            <div style={{ fontSize: 14, color: '#666' }}>Detected word (Arabic)</div>
            <div style={{ fontSize: 32, direction: 'rtl', textAlign: 'right' }}>{detectedWordAr || 'لا توجد كلمة بعد...'}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>({detectedWordEn.join('-') || 'No word yet...'})</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={addSpace}>Space</button>
              <button onClick={deleteLastChar}>Delete</button>
              <button onClick={clearAll}>Clear All</button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {!running ? (
              <button onClick={start} disabled={!model}>Start Webcam</button>
            ) : (
              <button onClick={stop}>Stop Webcam</button>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 24, padding: 16, border: '1px solid #eee', borderRadius: 8 }}>
        <h3 style={{ marginTop: 0 }}>Signs Reference</h3>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 4, fontWeight: 'bold' }}>Space Sign</div>
            <img 
              src={`${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/sign_space.jpeg`} 
              alt="Space Sign" 
              style={{ width: '150px', borderRadius: 8, border: '1px solid #ddd' }} 
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 4, fontWeight: 'bold' }}>Delete Sign</div>
            <img 
              src={`${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/sign_delete.jpeg`} 
              alt="Delete Sign" 
              style={{ width: '150px', borderRadius: 8, border: '1px solid #ddd' }} 
            />
          </div>
          <div style={{ flexBasis: '100%' }}>
            <div style={{ marginBottom: 4, fontWeight: 'bold' }}>Arabic Alphabet Signs</div>
            <img 
              src={`${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/arabic_signs.jpg`} 
              alt="Arabic Alphabet Signs" 
              style={{ width: '100%', maxWidth: '800px', borderRadius: 8, border: '1px solid #ddd' }} 
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        Tips:
        <ul>
          <li>Grant camera permission when prompted.</li>
          <li>If the page is blank, check the browser console for any model loading errors.</li>
          <li>The model expects a single hand; keep your hand within the frame.</li>
        </ul>
      </div>
    </div>
  );
}
